/**
 * Client lifecycle of the acute-case desk session: read the stored token,
 * log in, log out, and drop back to the login form when the server rejects
 * the role.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	clearDeskSession,
	type DeskRole,
	readDeskSession,
	writeDeskSession,
} from '@/lib/desk-session-storage'
import { trpc } from '@/trpc/client'

export interface DeskSession {
	role: DeskRole | null
	isLoggingIn: boolean
	error: string | null
	login: (role: DeskRole, password: string) => Promise<boolean>
	logout: () => void
	/**
	 * Re-reads storage — call when the drawer opens so a session that expired
	 * while the tab sat idle overnight is noticed before anything is rendered
	 * as logged in.
	 */
	refresh: () => void
	/** Wire desk mutations' onError here to recover from a dead token. */
	handleAuthError: (err: { data?: { code?: string } | null }) => void
}

/**
 * @param verify when true, the stored session is checked against the server.
 *   Pass the drawer's open state: there is no point spending a request on a
 *   session nobody is about to use.
 */
export function useDeskSession(verify = false): DeskSession {
	const utils = trpc.useUtils()
	const [role, setRole] = useState<DeskRole | null>(
		() => readDeskSession()?.role ?? null,
	)
	const [error, setError] = useState<string | null>(null)

	const loginMutation = trpc.voiceAgent.desk.login.useMutation()

	// The server is the authority on the session. A locally valid-looking token
	// can still be dead — secret rotated, TTL shortened, clock skew — and this
	// turns that into a clean login prompt instead of an error box later.
	const sessionQuery = trpc.voiceAgent.desk.session.useQuery(undefined, {
		enabled: verify && role !== null,
		staleTime: 60_000,
	})

	const refresh = useCallback(() => {
		setRole(readDeskSession()?.role ?? null)
	}, [])

	// Server says nobody is logged in while we think somebody is — believe the
	// server and drop the stored token.
	const serverRole = sessionQuery.data?.role
	const serverSaysAnonymous = sessionQuery.isSuccess && serverRole == null
	useEffect(() => {
		if (serverSaysAnonymous) {
			clearDeskSession()
			setRole(null)
		} else if (serverRole && serverRole !== role) {
			// Roles disagree (another tab logged in as the other desk) — the
			// server's answer is the one the procedures will enforce.
			setRole(serverRole)
		}
	}, [serverSaysAnonymous, serverRole, role])

	const logout = useCallback(() => {
		clearDeskSession()
		setRole(null)
		setError(null)
		// The header is gone now, so cached desk data belongs to nobody
		utils.voiceAgent.desk.listAcute.reset()
		utils.voiceAgent.desk.session.reset()
	}, [utils])

	const login = useCallback(
		async (nextRole: DeskRole, password: string) => {
			setError(null)
			try {
				const session = await loginMutation.mutateAsync({
					role: nextRole,
					password,
				})
				writeDeskSession({
					token: session.token,
					role: session.role,
					expiresAt: session.expiresAt,
				})
				setRole(session.role)
				// reset(), not invalidate(): the query may still be holding the 401
				// from the logged-out moment, and a stale error left in the cache
				// would be re-handled on the next render and log this new session
				// straight back out. Resetting drops the error along with the data.
				utils.voiceAgent.desk.listAcute.reset()
				utils.voiceAgent.desk.session.reset()
				return true
			} catch (err) {
				setError(
					(err as { message?: string }).message ?? 'Přihlášení se nezdařilo',
				)
				return false
			}
		},
		[loginMutation, utils],
	)

	const handleAuthError = useCallback(
		(err: { data?: { code?: string } | null }) => {
			// UNAUTHORIZED means the token is gone or expired server-side — the
			// only recovery is logging in again. FORBIDDEN is the wrong role for
			// this action and is left to the caller: the session is still valid.
			if (err.data?.code === 'UNAUTHORIZED') logout()
		},
		[logout],
	)

	// Stable identity so that destructured members stay referentially cheap for
	// consumers. (The stale-401-logs-you-straight-back-out bug is fixed by the
	// reset() above and by the drawer gating its error effect on `role`; this
	// memo is hygiene, not the fix.)
	return useMemo(
		() => ({
			role,
			isLoggingIn: loginMutation.isPending,
			error,
			login,
			logout,
			refresh,
			handleAuthError,
		}),
		[
			role,
			loginMutation.isPending,
			error,
			login,
			logout,
			refresh,
			handleAuthError,
		],
	)
}
